<template>
  <b-modal
    id="add-or-update-event-center"
    :visible="isAddOrUpdateActive"
    centered
    hide-footer
    hide-header
    no-close-on-backdrop
    no-close-on-esc
    @hidden="resetForm"
    @hide="resetForm"
    @change="(val) => $emit('update:is-add-or-update-active', val)"
  >
    <template #default="{ hide }">
      <b-overlay :show="loading" class="px-2">
        <div class="d-flex justify-content-between align-items-center content-sidebar-header py-1" style="border-bottom: 1px solid #000000;">
          <h5 class="mb-0" style="font-weight: 400; font-size: 14px; line-height: 160%;">
            {{ isUpdate ? 'Update' : 'Add New' }}
          </h5>

          <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="hide"
          />
        </div>

        <validation-observer
          #default="{ handleSubmit }"
          ref="update_modal_form"
        >
          <b-form
            class="my-1"
            @submit.prevent="handleSubmit(onCreateOrUpdate)"
            @reset.prevent="resetForm"
          >

            <b-alert
              v-if="errorMessage"
              variant="danger"
              :show="true"
            >
              <div class="alert-body font-small-2">
                <b-card-text class="mb-0">
                  {{ errorMessage }}
                </b-card-text>
              </div>
            </b-alert>

            <b-form-group label="Service Type" label-for="service_type">
              <template #label>
                <span>Service Type<sup style="color: tomato">*</sup></span>
              </template>

              <validation-provider #default="{ errors }" name="Service Type" rules="required">
                <v-select
                  id="service_type"
                  v-model="new_data.service_type"
                  placeholder="Service Type"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="serviceTypeOptions"
                  :reduce="(val) => val.value"
                  :disabled="loading"
                />

                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <validation-provider
              #default="validationContext"
              name="Year"
              rules="required|max:4|min:4"
            >
              <b-form-group
                label="Year"
                label-for="year"
              >
                <template #label>
                  <span>Year<sup style="color: tomato">*</sup></span>
                </template>

                <b-form-input
                  id="year"
                  v-model="new_data.year"
                  type="number"
                  autofocus
                  trim
                  placeholder="Eg: 2002"
                  :disabled="disableForm"
                  :state="getValidationState(validationContext)"
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <div v-if="new_data.service_type === 'Internal'">
              <b-form-group label="Region" label-for="region">
                <template #label>
                  <span>Region<sup style="color: tomato">*</sup></span>
                </template>

                <validation-provider #default="{ errors }" name="Region" rules="required">
                  <v-select
                    id="region"
                    v-model="new_data.region_id"
                    placeholder="Region"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="geoRegionOptions"
                    :reduce="(val) => val.value"
                    :disabled="loading"
                  />

                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group label="Area" label-for="Area">
                <template #label>
                  <span>Area<sup style="color: tomato">*</sup></span>
                </template>

                <validation-provider #default="{ errors }" name="Area" rules="required">
                  <v-select
                    id="Area"
                    v-model="new_data.area_id"
                    placeholder="Area"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="geoAreaOptions"
                    :reduce="(val) => val.value"
                    :disabled="loading"
                  />

                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group label="District" label-for="District">
                <template #label>
                  <span>District<sup style="color: gray">(optional)</sup></span>
                </template>

                <validation-provider #default="{ errors }" name="District">
                  <v-select
                    id="District"
                    v-model="new_data.district_id"
                    placeholder="District"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="geoDistrictOptions"
                    :reduce="(val) => val.value"
                    :disabled="loading"
                  />

                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group label="Assembly" label-for="Assembly">
                <template #label>
                  <span>Assembly<sup style="color: gray">(optional)</sup></span>
                </template>

                <validation-provider #default="{ errors }" name="Assembly">
                  <v-select
                    id="Assembly"
                    v-model="new_data.assembly_id"
                    placeholder="Assembly"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="geoAssemblyOptions"
                    :reduce="(val) => val.value"
                    :disabled="loading"
                  />

                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </div>

            <!-- Country -->
            <b-form-group v-else label="Country" label-for="Country">
              <template #label>
                <span>Country<span style="color: tomato">*</span></span>
              </template>

              <validation-provider
                #default="{ errors }"
                name="Country"
                rules="required"
              >
                <v-select
                  v-model="new_data.country_id"
                  :loading="loading"
                  :clearable="false"
                  :options="countryOptions"
                  :reduce="(val) => val.value"
                  :searchable="true"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  @search="onSearchCountry"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>


            <!-- Form Actions -->
            <div class="d-flex mt-2">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="mr-2"
                type="submit"
                :disabled="disableForm"
              >
                {{ isUpdate ? 'Update' : 'Add' }}
              </b-button>
              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="button"
                variant="outline-secondary"
                @click="hide"
              >
                Cancel
              </b-button>
            </div>
          </b-form>
        </validation-observer>
      </b-overlay>
    </template>
  </b-modal>
</template>

<script>
import {
  BOverlay, BSidebar, BForm, BFormGroup, BFormInput, 
  BFormTextarea, BFormInvalidFeedback, BButton, BFormSelect,
  BAlert, BCardText, BModal, BFormCheckbox, BFormDatepicker,
  BFormFile
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import { required } from '@validations'
import geoData from '@/@core/mixins/geoData';
import { get, cloneDeep, omit } from 'lodash';
import { ValidationProvider, ValidationObserver } from 'vee-validate'

import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'

const rawFormData = {
  region_id: "",
  area_id: "",
  district_id: "",
  assembly_id: "",
  country_id: "",
  year: "",
  service_type: "Internal",
}

export default {
  components: {
    BFormFile,
    BForm,
    BAlert,
    BModal,
    BButton,
    vSelect,
    BOverlay,
    BSidebar,
    BCardText,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BFormCheckbox,
    BFormDatepicker,
    BFormInvalidFeedback,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  mixins: [geoData],
  model: {
    prop: 'isAddOrUpdateActive',
    event: 'update:is-add-or-update-active',
  },
  props: {
    isAddOrUpdateActive: {
      type: Boolean,
      required: true,
    },
    updateData: {
      type: Object,
      default: null,
      required: false
    },
    userId: {
      type: String,
      required: false,
      default: ""
    },
    isPersonal: {
      type: Boolean,
      required: false,
      default: false
    },
  },
  data() {
    return {
      required,
      loading: false,
      errorMessage: "",
      employment_types: [
        "Full-time",
        "Part-time",
        "Contract",
        "Self-employed",
        "Temporary",
        "Freelance",
        "Internship",
        "Remote",
        "Volunteer"
      ].map(type => ({ text: type, value: type })),
      location_types: [
        "On-site",
        "Hybrid",
        "Remote",
      ].map(type => ({ text: type, value: type })),
      serviceTypeOptions: [
        "Internal",
        "External",
      ].map(type => ({ label: type, value: type })),
      new_data: cloneDeep(rawFormData),
      editing_attachments: [],
      attachments: [],
      stationsServed: [],
      countryOptions: [],
    }
  },
  computed: {
    isUpdate(){
      return !!this.updateData
    },
    disableForm(){
      return false
    },
  },
  watch: {
    updateData: {
      handler(updateData){
        if (updateData){
          this.new_data = {
            _id: updateData._id,
            region_id: updateData.region_id,
            area_id: updateData.area_id,
            district_id: updateData.district_id,
            assembly_id: updateData.assembly_id,
            year: updateData.year,
          }
        }
      },
      immediate: true,
      deep: true
    },
    'new_data.region_id': {
      handler(region_id) {
        if (region_id) {
          this.fetchGeoAreas(region_id)
        }
      }
    },
    'new_data.area_id': {
      handler(area_id) {
        if (area_id) {
          this.fetchGeoDistricts(area_id)
        }
      }
    },
    'new_data.district_id': {
      handler(district) {
        if (district) {
          this.fetchGeoAssemblies(district)
        }
      }
    }
  },
  created(){
    this.fetchGeoRegions()
  },
  methods: {
    async onSearchCountry(search, loading) {
      try {
        loading(true);

        if (!search) {
          return;
        }

        const query = {
          search,
        }

        const request = await this.useJwt().sharedService.fetchCountries(query);
        const { data } = request.data;

        this.countryOptions = data.map((item) => ({
          label: item.country,
          value: item._id 
        }))
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        loading(false);
      }
    },
    async onCreateOrUpdate(){
      try {
        this.loading = true;
        this.errorMessage = "";

        const success = await this.$refs.update_modal_form.validate();
        if (!success){
          return;
        }

        const messages = {
          update: {
            title: "Confirm Update",
            text: "Proceed to update record?",
            icon: "warning"
          },
          new: {
            title: "Create",
            text: "Proceed to create new record?",
            icon: "info"
          }
        }

        /* eslint-disable no-nested-ternary */
        const message = this.isUpdate ? messages.update : messages.new;
        const { title, text, icon } = message;

        const result = await this.$swal({
          title,
          text,
          icon,
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });
        if (!result.isConfirmed) {
          return;
        }

        const update_data = omit(this.new_data, ["_id"]);
        const id = this.new_data._id

        if (!update_data.district_id) {
          // or set it to null?
          delete update_data.district_id
        }

        if (!update_data.assembly_id) {
          delete update_data.assembly_id
        }

        const apiService = this.useJwt().mainService.stationsServed;
        
        if (this.isUpdate){
          await apiService.update(id, update_data);
        } else {
          update_data.user_id = this.userId
          await apiService.create(update_data);
        }
        this.$emit("refetchData");
        this.$emit('update:is-add-or-update-active', false);

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            icon: 'AlertQuestionIcon',
            variant: 'success',
            text: `${this.isUpdate ? 'Updated' : 'Added'} successfully`
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    resetForm() {
      this.$emit("resetUpdateData")
      this.new_data = {};
      if (this.$refs.update_modal_form) {
        this.$refs.update_modal_form.reset()
      }
    },
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

#add-or-update-event-center {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}
</style>
